import type {
  EditableAvailabilityDay,
  EditableAvailabilitySettings,
} from './availabilityTransforms';
import { AVAILABILITY_WEEKDAYS } from './availabilityTransforms';
import type { AvailabilityWeekday } from './availabilityTypes';

const MINUTES_PER_DAY = 24 * 60;
const TIME_PATTERN = /^([01]\d|2[0-3]):([0-5]\d)$/;

export interface AvailabilityValidationResult {
  valid: boolean;
  errors: string[];
  dayErrors: Partial<Record<AvailabilityWeekday, string[]>>;
}

interface ParsedWindow {
  startMinute: number;
  endMinute: number;
}

export function validateEditableAvailability(
  settings: EditableAvailabilitySettings,
  persistedTimeZone?: string
): AvailabilityValidationResult {
  const errors = validateSettingsFields(settings, persistedTimeZone);
  const dayErrors = validateDays(settings.days);
  return {
    valid: errors.length === 0 && Object.keys(dayErrors).length === 0,
    errors,
    dayErrors,
  };
}

export function parseHHMM(value: string): number | null {
  const match = TIME_PATTERN.exec(value.trim());
  if (!match) {
    return null;
  }
  return Number(match[1]) * 60 + Number(match[2]);
}

export function windowsOverlap(
  left: { start_time: string; end_time: string },
  right: { start_time: string; end_time: string }
): boolean {
  const leftWindow = parseWindow(left);
  const rightWindow = parseWindow(right);
  if (!leftWindow || !rightWindow) {
    return false;
  }
  return (
    leftWindow.startMinute < rightWindow.endMinute && rightWindow.startMinute < leftWindow.endMinute
  );
}

function validateSettingsFields(
  settings: EditableAvailabilitySettings,
  persistedTimeZone?: string
): string[] {
  const errors: string[] = [];
  const timeZone = settings.time_zone.trim();
  const preservesLegacyLocal = timeZone === 'Local' && persistedTimeZone?.trim() === 'Local';
  if (!timeZone) {
    errors.push('Time zone is required.');
  } else if (!preservesLegacyLocal && !isValidTimeZone(timeZone)) {
    errors.push('Time zone must be a valid IANA time zone.');
  }
  const bookingIntervalMinutes = parseEditableMinuteValue(settings.booking_interval_minutes);
  if (
    bookingIntervalMinutes === null ||
    bookingIntervalMinutes <= 0 ||
    bookingIntervalMinutes > MINUTES_PER_DAY
  ) {
    errors.push('Booking interval must be between 1 and 1440 minutes.');
  }
  const bufferBeforeMinutes = parseEditableMinuteValue(settings.buffer_before_minutes);
  if (bufferBeforeMinutes === null || bufferBeforeMinutes < 0) {
    errors.push('Buffer before must be non-negative.');
  }
  const bufferAfterMinutes = parseEditableMinuteValue(settings.buffer_after_minutes);
  if (bufferAfterMinutes === null || bufferAfterMinutes < 0) {
    errors.push('Buffer after must be non-negative.');
  }
  const minimumNoticeMinutes = parseEditableMinuteValue(settings.minimum_notice_minutes);
  if (minimumNoticeMinutes === null || minimumNoticeMinutes < 0) {
    errors.push('Minimum notice must be non-negative.');
  }
  return errors;
}

function parseEditableMinuteValue(value: number | ''): number | null {
  if (value === '' || !Number.isFinite(value)) {
    return null;
  }
  return value;
}

function validateDays(
  days: EditableAvailabilityDay[]
): Partial<Record<AvailabilityWeekday, string[]>> {
  const errors: Partial<Record<AvailabilityWeekday, string[]>> = {};
  const seenWeekdays = new Set<AvailabilityWeekday>();

  for (const day of days) {
    if (!isAvailabilityWeekday(day.weekday)) {
      continue;
    }
    const weekdayErrors = validateDay(day);
    if (seenWeekdays.has(day.weekday)) {
      weekdayErrors.push('Weekday appears more than once.');
    }
    seenWeekdays.add(day.weekday);
    if (weekdayErrors.length > 0) {
      errors[day.weekday] = weekdayErrors;
    }
  }

  for (const weekday of AVAILABILITY_WEEKDAYS) {
    if (!seenWeekdays.has(weekday)) {
      errors[weekday] = [...(errors[weekday] ?? []), 'Weekday is missing.'];
    }
  }
  return errors;
}

function validateDay(day: EditableAvailabilityDay): string[] {
  if (!day.enabled) {
    return day.windows.length > 0 ? ['Unavailable days cannot have availability windows.'] : [];
  }
  if (day.windows.length === 0) {
    return ['Available days must have at least one availability window.'];
  }

  const errors: string[] = [];
  const parsedWindows: ParsedWindow[] = [];
  for (const window of day.windows) {
    const parsed = parseWindow(window);
    if (!parsed) {
      errors.push('Availability windows must use HH:mm times.');
      continue;
    }
    if (parsed.startMinute >= parsed.endMinute) {
      errors.push('Availability window start time must be before end time.');
      continue;
    }
    parsedWindows.push(parsed);
  }

  const sortedWindows = parsedWindows
    .slice()
    .sort((left, right) => left.startMinute - right.startMinute);
  for (let index = 1; index < sortedWindows.length; index += 1) {
    const previous = sortedWindows[index - 1];
    const current = sortedWindows[index];
    if (current.startMinute < previous.endMinute) {
      errors.push('Availability windows cannot overlap.');
      break;
    }
  }
  return Array.from(new Set(errors));
}

function parseWindow(window: { start_time: string; end_time: string }): ParsedWindow | null {
  const startMinute = parseHHMM(window.start_time);
  const endMinute = parseHHMM(window.end_time);
  if (startMinute === null || endMinute === null) {
    return null;
  }
  return { startMinute, endMinute };
}

function isValidTimeZone(timeZone: string): boolean {
  try {
    Intl.DateTimeFormat(undefined, { timeZone: timeZone.trim() }).format(new Date());
    return true;
  } catch {
    return false;
  }
}

function isAvailabilityWeekday(value: number): value is AvailabilityWeekday {
  return AVAILABILITY_WEEKDAYS.includes(value as AvailabilityWeekday);
}
